/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { syncProductsStockBySkus } from 'api/products'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { axios } from 'api'
import { useTranslation } from 'react-i18next'

export type Product = {
  id: string
  sku: string
  itemClass: string
  color: string
  itemCategory: string
  cabinetType: string
  detail: string
  description: string
  weight: number
  price: number
  stock: number
  afmx: number
  isHingeable: boolean
  imageIndex: string
  urlKey: string
  qtyRestrictions: number
}

export type Item = {
  cartItemId: string
  productId: string
  sku: string
  itemClass: string
  color?: string
  itemCategory?: string
  cabinetType?: string
  detail: string
  description?: string
  weight: number
  price: number
  quantity: number
  afmx: number
  isHingeable: boolean
  isAssembled: boolean
  hingeDirection: string
  imageIndex: string
  qtyRestrictions: number
}

const useProduct = () => {
  const { t } = useTranslation()
  const [product, setProduct] = useState<Product | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const { productId } = useParams<{ productId: string }>()
  const [stock, setStock] = useState<number | null>(null)
  const [stockLoading, setStockLoading] = useState(false)
  const { showMessage } = useContext(SnackbarContext)

  // Reload only when the route's product id changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchProductById = async (productId: string) => {
      if (productId) {
        const response = await axios.get(`/products/${productId}`)
        if (response.status === 200) {
          setProduct(response.data)
        } else {
          setProduct(null)
          showMessage(t('snackbar.product.fetchError'), 'error')
        }
      }
    }

    const loadProduct = async () => {
      if (productId) {
        await fetchProductById(productId)
        setHasLoaded(true)
      }
    }

    loadProduct()
  }, [productId])

  useEffect(() => {
    const checkStock = async () => {
      if (product) {
        setStock(null)
        setStockLoading(true)
        const products = await syncProductsStockBySkus([product.sku])
        setStock(products[0] ? Number(products[0].stock) : null)
        setStockLoading(false)
      }
    }

    if (product) checkStock()
  }, [product])

  return {
    product,
    stock,
    stockLoading,
    hasLoaded
  }
}

export default useProduct
